export interface MarketDataUpdate {
  coin: string;
  markPrice: number;
  oraclePrice: number;
  funding: number;
  openInterest: number;
  volume: number;
  timestamp: number;
}

type MarketDataCallback = (data: MarketDataUpdate) => void;

interface CoinSubscription {
  coin: string;
  dex?: string; // For HIP-3 markets
}

export class MarketDataWebSocket {
  private ws: WebSocket | null = null;
  private callbacks: Set<MarketDataCallback> = new Set();
  private subscribedCoins: Map<string, CoinSubscription> = new Map();
  private reconnectTimer: number | null = null;
  private isConnecting: boolean = false;
  private wsUrl: string;
  private activeDex: string | null = null; // Track the active dex subscription

  constructor(wsUrl: string = 'wss://api.hyperliquid.xyz/ws') {
    this.wsUrl = wsUrl;
  }

  subscribe(coin: string, callback: MarketDataCallback, dex?: string) {
    // Extract dex from coin if not provided (e.g., "xyz:XYZ100" -> dex="xyz", coin="XYZ100")
    let actualCoin = coin;
    let actualDex = dex;

    if (!actualDex && coin.includes(':')) {
      const parts = coin.split(':');
      actualDex = parts[0];
      actualCoin = parts[1];
    }

    // If switching to a different dex, we need to reconnect
    if (actualDex && this.activeDex && actualDex !== this.activeDex) {
      this.subscribedCoins.clear();
      this.disconnect();
    }

    this.callbacks.add(callback);
    this.subscribedCoins.set(coin, { coin: actualCoin, dex: actualDex });

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connect();
    } else {
      // Already connected, subscribe to this coin's dex if not already subscribed
      if (actualDex && actualDex !== this.activeDex) {
        this.subscribeToData(actualDex);
      }
    }
  }

  unsubscribe(callback: MarketDataCallback) {
    this.callbacks.delete(callback);

    // If no more callbacks, disconnect and clear subscriptions
    if (this.callbacks.size === 0) {
      this.subscribedCoins.clear();
      this.activeDex = null;
      this.disconnect();
    }
  }

  private connect() {
    if (this.isConnecting) return;

    this.isConnecting = true;
    console.log('Connecting to market data WebSocket');

    try {
      this.ws = new WebSocket(this.wsUrl);

      // Store reference to current WebSocket to prevent race conditions
      const currentWs = this.ws;

      currentWs.onopen = () => {
        // Only proceed if this WebSocket is still the active one
        if (this.ws !== currentWs) {
          console.log('Market data WebSocket opened but no longer active, ignoring');
          currentWs.close();
          return;
        }

        console.log('Market data WebSocket connected');
        this.isConnecting = false;

        // Subscribe to the dex if we have subscribed coins
        if (this.subscribedCoins.size > 0) {
          // Get the first subscription's dex
          const firstSub = Array.from(this.subscribedCoins.values())[0];
          if (firstSub.dex) {
            this.subscribeToData(firstSub.dex);
          } else {
            // Native markets - subscribe without dex
            this.subscribeToData();
          }
        }
      };

      currentWs.onmessage = (event) => {
        // Only process messages if this WebSocket is still the active one
        if (this.ws !== currentWs) {
          return;
        }
        try {
          const message = JSON.parse(event.data);

          // Handle allMids channel (mark prices)
          if (message.channel === 'allMids' && message.data) {
            const data = message.data;

            // Process mid prices
            Object.entries(data.mids as Record<string, string>).forEach(([apiCoin, midPrice]) => {
              // For HIP-3 markets, the API returns just the coin name (e.g., "XYZ100")
              // but we're subscribed with the full format (e.g., "xyz:XYZ100")
              // So we need to match against both formats
              this.subscribedCoins.forEach((subscription, subscribedKey) => {
                const matches =
                  subscribedKey === apiCoin || // Direct match (native)
                  subscription.coin === apiCoin || // Match on just the coin part (HIP-3)
                  subscribedKey === `${subscription.dex}:${apiCoin}`; // Match full format

                if (matches) {
                  const update: MarketDataUpdate = {
                    coin: subscribedKey, // Use the original subscription key
                    markPrice: parseFloat(midPrice),
                    oraclePrice: parseFloat(midPrice), // Same as mark for now
                    funding: 0, // Will be updated by notification channel
                    openInterest: 0, // Will be updated by meta channel
                    volume: 0, // Will be updated by meta channel
                    timestamp: Date.now(),
                  };

                  this.callbacks.forEach(callback => callback(update));
                }
              });
            });
          }

          // Handle notification channel (funding updates)
          if (message.channel === 'notification' && message.data?.notification) {
            const notif = message.data.notification;
            if (notif.type === 'funding') {
              // Extract funding data if available
              console.log('Funding update:', notif);
            }
          }
        } catch (error) {
          console.error('Error parsing market data WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('Market data WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = () => {
        console.log('Market data WebSocket closed');
        this.isConnecting = false;
        this.ws = null;

        // Attempt to reconnect if we still have callbacks
        if (this.callbacks.size > 0) {
          this.reconnectTimer = window.setTimeout(() => {
            this.connect();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('Error creating market data WebSocket:', error);
      this.isConnecting = false;
    }
  }

  private subscribeToData(dex?: string) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    // For HIP-3 markets, subscribe to allMids with dex parameter
    // For native markets, subscribe without dex
    const subscribeMessage: any = {
      method: 'subscribe',
      subscription: {
        type: 'allMids',
      },
    };

    if (dex) {
      subscribeMessage.subscription.dex = dex;
      this.activeDex = dex;
    } else {
      this.activeDex = null;
    }

    this.ws.send(JSON.stringify(subscribeMessage));
  }

  private disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      // Only close if connection is OPEN or CLOSING, not if CONNECTING
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CLOSING) {
        this.ws.close();
      }
      this.ws = null;
    }

    this.subscribedCoins.clear();
    this.activeDex = null;
    this.isConnecting = false;
  }

  cleanup() {
    this.callbacks.clear();
    this.disconnect();
  }
}

export const marketDataWs = new MarketDataWebSocket();
